import { useEffect, useState } from 'react';
import { Admin, PasswordRequirements } from '../../../clients/quipli-service/admin/admin';

const SPECIAL_CHARACTERS_REGEX = /[!@#$%^&*{};'[\]"]/g;
const SPECIAL_CHARACTERS = `!@#$%^&*{};'[]"`;

export type AdminFormType = 'create' | 'setup' | 'changePassword' | 'changeSelfPassword';

export interface UseAdminFormStateProps {
  admin?: Admin;
  type?: AdminFormType;
  passwordRequirements?: PasswordRequirements;
}

export interface UseAdminFormStateReturn {
  // Form state
  adminUsername: string;
  adminFullName: string;
  adminEmail: string;
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  errorMessage: string | undefined;
  isDisabled: boolean;

  // Form actions
  setUsername: (username: string) => void;
  setFullname: (fullname: string) => void;
  setEmail: (email: string) => void;
  setCurrentPassword: (password: string) => void;
  setNewPassword: (password: string) => void;
  setConfirmNewPassword: (password: string) => void;

  // Validation functions
  checkConfirmPassword: () => boolean;
  validatePassword: (password: string) => string | undefined;
}

export const useAdminFormState = ({
  admin,
  type = 'create',
  passwordRequirements,
}: UseAdminFormStateProps): UseAdminFormStateReturn => {
  // Form state
  const [adminUsername, setUsername] = useState<string>('');
  const [adminFullName, setFullname] = useState<string>('');
  const [adminEmail, setEmail] = useState<string>('');
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // Initialize form with admin user data
  useEffect(() => {
    if (admin) {
      setUsername(admin.adminUsername);
      setFullname(admin.adminFullName);
      setEmail(admin.adminEmail);
    }
  }, [admin]);

  // Password validation effect
  useEffect(() => {
    if (passwordRequirements && newPassword) {
      const validationError = validatePassword(newPassword);
      setErrorMessage(validationError);
    } else {
      setErrorMessage(undefined);
    }
  }, [newPassword, confirmNewPassword, passwordRequirements]);

  // Form validation effect
  useEffect(() => {
    if ((type === 'create' || type === 'changePassword' || type === 'changeSelfPassword') && !passwordRequirements) {
      setIsDisabled(true);
      return;
    }
    if (errorMessage) {
      setIsDisabled(true);
      return;
    }

    setIsDisabled(
      adminUsername.length === 0 ||
        adminFullName.length === 0 ||
        adminEmail.length === 0 ||
        ((type === 'changePassword' || type === 'changeSelfPassword') && newPassword.length === 0) ||
        (type === 'changeSelfPassword' && currentPassword.length === 0),
    );
  }, [
    adminUsername,
    adminFullName,
    adminEmail,
    newPassword,
    currentPassword,
    errorMessage,
    passwordRequirements,
    type,
  ]);

  const checkConfirmPassword = () => {
    if (newPassword !== confirmNewPassword) {
      return false;
    }
    return true;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!passwordRequirements) return undefined;

    if (type === 'changePassword' || type === 'create' || type === 'changeSelfPassword') {
      if (password.length < passwordRequirements.minimumLength) {
        return `Password must be at least ${passwordRequirements.minimumLength} character(s) long`;
      }

      const lowercaseCharCount = password.length - password.replaceAll(/[a-z]/g, '').length;
      if (passwordRequirements.lowercaseCharacters > lowercaseCharCount) {
        return `Password must contain at least ${passwordRequirements.lowercaseCharacters} lowercase letter(s)`;
      }

      const uppercaseCharCount = password.length - password.replaceAll(/[A-Z]/g, '').length;
      if (passwordRequirements.uppercaseCharacters > uppercaseCharCount) {
        return `Password must contain at least ${passwordRequirements.uppercaseCharacters} uppercase letter(s)`;
      }

      const numericCharCount = password.length - password.replaceAll(/[0-9]/g, '').length;
      if (passwordRequirements.numericCharacters > numericCharCount) {
        return `Password must contain at least ${passwordRequirements.numericCharacters} number(s)`;
      }

      const specialCharCount = password.length - password.replaceAll(SPECIAL_CHARACTERS_REGEX, '').length;
      if (passwordRequirements.specialCharacters > specialCharCount) {
        return `Password must contain at least ${passwordRequirements.specialCharacters} special character(s) from ${SPECIAL_CHARACTERS}`;
      }

      if (newPassword !== confirmNewPassword) {
        return 'Passwords do not match';
      }
    }

    return undefined;
  };

  return {
    // Form state
    adminUsername,
    adminFullName,
    adminEmail,
    currentPassword,
    newPassword,
    confirmNewPassword,
    errorMessage,
    isDisabled,

    // Form actions
    setUsername,
    setFullname,
    setEmail,
    setCurrentPassword,
    setNewPassword,
    setConfirmNewPassword,

    // Validation functions
    checkConfirmPassword,
    validatePassword,
  };
};
